import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2, XCircle, MinusCircle, Download, Pencil, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DURUM_MAP = {
  uygun: { label: 'Uygun', color: '#22c55e', icon: CheckCircle2, bg: 'bg-green-900/20' },
  uygunsuz: { label: 'Uygunsuz', color: '#ef4444', icon: XCircle, bg: 'bg-red-900/20' },
  kontrol_edilmedi: { label: 'Kontrol Edilmedi', color: '#6b7280', icon: MinusCircle, bg: 'bg-gray-800/30' },
};

export default function ISYGenelSartname({ ihaleler, onBack }) {
  const [selectedIhale, setSelectedIhale] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ toplam: 0, uygun: 0, uygunsuz: 0, kontrol_edilmedi: 0, skor: 0 });
  const [loading, setLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({ durum: '', aciklama: '', kontrol_tarihi: '', kontrol_eden: '' });

  const fetchSartname = useCallback(async (ihaleId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/isy/sartname/${ihaleId}`, { withCredentials: true });
      setItems(res.data.items || []);
      setStats({ toplam: res.data.toplam, uygun: res.data.uygun, uygunsuz: res.data.uygunsuz, kontrol_edilmedi: res.data.kontrol_edilmedi, skor: res.data.skor });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedIhale) fetchSartname(selectedIhale.id); }, [selectedIhale, fetchSartname]);

  const handleSeed = async () => {
    try {
      const res = await axios.post(`${API}/isy/sartname/${selectedIhale.id}/seed`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchSartname(selectedIhale.id);
    } catch (e) { toast.error('Yükleme hatası'); }
  };

  const openEdit = (item) => {
    setEditForm({
      madde_no: item.madde_no, madde_adi: item.madde_adi,
      durum: item.durum || 'kontrol_edilmedi', aciklama: item.aciklama || '',
      kontrol_tarihi: item.kontrol_tarihi || new Date().toISOString().split('T')[0],
      kontrol_eden: item.kontrol_eden || ''
    });
    setEditItem(item);
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/isy/sartname/item/${editItem.id}`, editForm, { withCredentials: true });
      toast.success('Güncellendi');
      setEditItem(null);
      fetchSartname(selectedIhale.id);
    } catch (e) { toast.error('Güncelleme hatası'); }
  };

  const handleQuickStatus = async (item, durum) => {
    try {
      await axios.put(`${API}/isy/sartname/item/${item.id}`, {
        madde_no: item.madde_no, madde_adi: item.madde_adi,
        durum, kontrol_tarihi: new Date().toISOString().split('T')[0]
      }, { withCredentials: true });
      fetchSartname(selectedIhale.id);
    } catch (e) { toast.error('Hata'); }
  };

  if (!selectedIhale) {
    return (
      <div className="space-y-4" data-testid="isy-genel-sartname">
        <h2 className="text-lg font-bold text-white">Genel Şartname Uyum Kontrolü</h2>
        <p className="text-sm text-gray-400">Kontrol yapmak istediğiniz ihaleyi seçin:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(ihaleler || []).map(ih => (
            <Card key={ih.id} className="bg-gray-900/60 border-gray-700/50 hover:border-green-500/40 cursor-pointer transition-colors" onClick={() => setSelectedIhale(ih)} data-testid={`sartname-select-${ih.id}`}>
              <CardContent className="py-3 px-4">
                <p className="text-sm font-semibold text-white">{ih.is_adi}</p>
                <p className="text-xs text-gray-500 mt-1">Uyum Skoru: {ih.genel_sartname_uyum_skoru ? `%${ih.genel_sartname_uyum_skoru}` : 'Henüz yok'}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const skorColor = stats.skor >= 80 ? '#22c55e' : stats.skor >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <div className="space-y-4" data-testid="isy-genel-sartname">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedIhale(null)} className="text-gray-400 hover:text-white h-8 px-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <div>
            <h2 className="text-sm font-bold text-white">{selectedIhale.is_adi}</h2>
            <p className="text-xs text-gray-500">YİGŞ Uyum Kontrolü</p>
          </div>
        </div>
      </div>

      {/* Skor Kartları */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card className="bg-gray-900/80 border-gray-700/50 lg:col-span-1">
          <CardContent className="pt-4 pb-3 text-center">
            <Shield className="w-6 h-6 mx-auto mb-1" style={{ color: skorColor }} />
            <p className="text-2xl font-bold" style={{ color: skorColor }}>%{stats.skor}</p>
            <p className="text-xs text-gray-500">Uyum Skoru</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-lg font-bold text-white">{stats.toplam}</p>
            <p className="text-xs text-gray-500">Toplam Madde</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-lg font-bold text-green-400">{stats.uygun}</p>
            <p className="text-xs text-gray-500">Uygun</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-lg font-bold text-red-400">{stats.uygunsuz}</p>
            <p className="text-xs text-gray-500">Uygunsuz</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <p className="text-lg font-bold text-gray-400">{stats.kontrol_edilmedi}</p>
            <p className="text-xs text-gray-500">Kontrol Edilmedi</p>
          </CardContent>
        </Card>
      </div>

      {/* Madde Listesi */}
      {loading ? (
        <div className="text-center text-gray-400 py-8">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <Card className="bg-gray-900/60 border-gray-700/50">
          <CardContent className="py-8 text-center">
            <p className="text-gray-400 mb-3">YİGŞ maddeleri henüz yüklenmemiş.</p>
            <Button onClick={handleSeed} className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="sartname-seed-btn">
              <Download className="w-4 h-4 mr-1" /> Standart Maddeleri Yükle
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const d = DURUM_MAP[item.durum] || DURUM_MAP.kontrol_edilmedi;
            const Icon = d.icon;
            return (
              <Card key={item.id} className={`${d.bg} border-gray-700/30 transition-colors`} data-testid={`sartname-item-${item.id}`}>
                <CardContent className="py-2.5 px-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Icon className="w-5 h-5 flex-shrink-0" style={{ color: d.color }} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-mono">Md.{item.madde_no}</span>
                          <span className="text-sm text-white">{item.madde_adi}</span>
                        </div>
                        {item.aciklama && <p className="text-xs text-gray-400 mt-0.5 truncate">{item.aciklama}</p>}
                        {item.kontrol_tarihi && <span className="text-[10px] text-gray-600">{item.kontrol_tarihi} {item.kontrol_eden && `- ${item.kontrol_eden}`}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => handleQuickStatus(item, 'uygun')} className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${item.durum === 'uygun' ? 'bg-green-600' : 'bg-gray-800 hover:bg-green-900/50'}`} title="Uygun">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-300" />
                      </button>
                      <button onClick={() => handleQuickStatus(item, 'uygunsuz')} className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${item.durum === 'uygunsuz' ? 'bg-red-600' : 'bg-gray-800 hover:bg-red-900/50'}`} title="Uygunsuz">
                        <XCircle className="w-3.5 h-3.5 text-red-300" />
                      </button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-amber-400" onClick={() => openEdit(item)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Madde Kontrolü - Md.{editForm.madde_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-gray-400 text-xs">Madde</Label>
              <p className="text-sm text-white">{editForm.madde_adi}</p>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Durum</Label>
              <Select value={editForm.durum} onValueChange={v => setEditForm({...editForm, durum: v})}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="uygun" className="text-green-400">Uygun</SelectItem>
                  <SelectItem value="uygunsuz" className="text-red-400">Uygunsuz</SelectItem>
                  <SelectItem value="kontrol_edilmedi" className="text-gray-400">Kontrol Edilmedi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Açıklama / Not</Label>
              <Input value={editForm.aciklama} onChange={e => setEditForm({...editForm, aciklama: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-400 text-xs">Kontrol Tarihi</Label>
                <Input type="date" value={editForm.kontrol_tarihi} onChange={e => setEditForm({...editForm, kontrol_tarihi: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Kontrol Eden</Label>
                <Input value={editForm.kontrol_eden} onChange={e => setEditForm({...editForm, kontrol_eden: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditItem(null)} className="text-gray-400">İptal</Button>
            <Button onClick={handleUpdate} className="bg-[#1e3a5f] hover:bg-[#2a4f7f] text-white" data-testid="sartname-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
